import React from 'react';
import { Link } from 'react-router-dom';
import ScreenTimeCard from '../components/ScreenTimeCard';
import GoalProgressCard from '../components/GoalProgressCard';
import AppUsageList from '../components/AppUsageList';
import UsageChart from '../components/UsageChart';
import AIInsightDashboardCard from '../components/AIInsightDashboardCard';

const DashboardPage = () => {
  // Mock data for screen time
  const screenTimeData = {
    today: 4.5,
    yesterday: 5.2,
    weekAverage: 4.8
  };

  // Mock data for app usage
  const appUsageData = [
    { app: 'TikTok', timeSpent: 85, icon: '📱', category: 'Social Media' },
    { app: 'YouTube', timeSpent: 67, icon: '🎬', category: 'Entertainment' },
    { app: 'Instagram', timeSpent: 45, icon: '📸', category: 'Social Media' },
    { app: 'Minecraft', timeSpent: 38, icon: '🎮', category: 'Gaming' },
    { app: 'Discord', timeSpent: 29, icon: '💬', category: 'Social' }
  ];

  // Mock data for goals
  const goalsData = [
    { 
      id: 1, 
      name: 'Limit social media', 
      current: 130, 
      target: 120, 
      unit: 'min',
      icon: '📱' 
    },
    { 
      id: 2, 
      name: 'Read e-books', 
      current: 25, 
      target: 30, 
      unit: 'min',
      icon: '📚' 
    },
    { 
      id: 3, 
      name: 'Focus time', 
      current: 45, 
      target: 60, 
      unit: 'min',
      icon: '🎯' 
    }
  ];

  // Mock data for usage chart
  const chartData = [
    { day: 'Mon', hours: 3.5 },
    { day: 'Tue', hours: 4.2 },
    { day: 'Wed', hours: 3.8 },
    { day: 'Thu', hours: 5.1 },
    { day: 'Fri', hours: 4.9 },
    { day: 'Sat', hours: 6.2 },
    { day: 'Sun', hours: 5.5 }
  ];

  // Teen-specific insights
  const teenInsights = [
    {
      id: 1,
      type: 'warning',
      title: 'Social Media vs. Homework',
      description: 'You\'re spending 3x more time on social apps than educational apps on school nights.',
      actionPath: '/habits'
    },
    {
      id: 2,
      type: 'positive',
      title: 'Gaming Balance',
      description: 'Great job keeping gaming under 40 minutes today - you met your goal!',
      actionPath: '/habits'
    },
    {
      id: 3,
      type: 'suggestion',
      title: 'Evening Screen Time',
      description: 'Using your phone after 10PM may be affecting your sleep quality and focus at school.',
      actionPath: '/settings'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome message with teen focus */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Hey there! 👋</h1>
        <p className="text-gray-600 mt-1">
          Here's your digital wellbeing snapshot for today. Our AI has some insights just for you.
        </p>
      </div>

      {/* Top stats row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow p-6">
          <div className="text-xs uppercase text-gray-500 font-semibold">Screen Time Today</div>
          <div className="flex items-baseline mt-1">
            <div className="text-2xl font-bold text-gray-900">{screenTimeData.today} hrs</div>
            <span className="ml-2 text-sm text-indigo-600">
              {screenTimeData.today < screenTimeData.yesterday ? 
                `↓ ${((screenTimeData.yesterday - screenTimeData.today) / screenTimeData.yesterday * 100).toFixed(0)}%` : 
                `↑ ${((screenTimeData.today - screenTimeData.yesterday) / screenTimeData.yesterday * 100).toFixed(0)}%`}
            </span>
          </div>
          <div className="text-xs text-gray-500 mt-1">vs. yesterday</div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="text-xs uppercase text-gray-500 font-semibold">Most Used App</div>
          <div className="flex items-center mt-2">
            <span className="text-xl mr-2">{appUsageData[0].icon}</span>
            <div>
              <div className="text-xl font-bold text-gray-900">{appUsageData[0].app}</div>
              <div className="text-sm text-gray-500">{Math.round(appUsageData[0].timeSpent)} minutes today</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="text-xs uppercase text-gray-500 font-semibold">Homework Focus Score</div>
          <div className="flex items-baseline mt-1">
            <div className="text-2xl font-bold text-gray-900">7.5/10</div>
            <span className="ml-2 text-sm text-green-600">↑ 15%</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Better than last week
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="text-xs uppercase text-gray-500 font-semibold">Late Night Usage</div>
          <div className="flex items-baseline mt-1">
            <div className="text-2xl font-bold text-gray-900">32 min</div>
            <span className="ml-2 text-sm text-red-600">after 10PM</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            May affect your sleep quality
          </div>
        </div>
      </div>

      {/* AI Insights Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Insights & Recommendations</h2>
          <Link to="/ai-insights" className="text-indigo-600 text-sm font-medium hover:text-indigo-700">
            View all insights →
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {teenInsights.map(insight => (
            <div key={insight.id} className={`bg-white rounded-xl shadow overflow-hidden
              ${insight.type === 'warning' ? 'border-l-4 border-red-500' : 
                insight.type === 'positive' ? 'border-l-4 border-green-500' : 
                'border-l-4 border-blue-500'}`}>
              <div className="p-5">
                <div className={`text-sm font-semibold mb-2
                  ${insight.type === 'warning' ? 'text-red-600' : 
                    insight.type === 'positive' ? 'text-green-600' : 
                    'text-blue-600'}`}>
                  {insight.title}
                </div>
                <p className="text-gray-600 text-sm mb-3">{insight.description}</p>
                <Link to={insight.actionPath} className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
                  See recommendation →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main dashboard content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow mb-8">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Weekly Screen Time</h2>
              <div className="h-80">
                <UsageChart data={chartData} timeRange="week" />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Screen Time Today</h2>
                <ScreenTimeCard screenTime={screenTimeData} />
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Goal Progress</h2>
                <GoalProgressCard goals={goalsData} />
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <div className="bg-white rounded-xl shadow">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">App Usage</h2>
                <Link to="/habits" className="text-sm text-indigo-600 hover:text-indigo-800">Set limits</Link>
              </div>
              <AppUsageList appUsage={appUsageData} />
            </div>
          </div>
        </div>
      </div>

      {/* AI-Powered Features section */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">AI-Powered Teen Insights</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <AIInsightDashboardCard 
            title="Personalized Insights" 
            description="Our AI analyzes your screen habits to find patterns that impact your school, sleep, and social life."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            }
            path="/ai-insights?tab=insights"
          />
          
          <AIInsightDashboardCard 
            title="Usage Predictions" 
            description="See how your current habits could affect your focus and sleep in the next week, with teen-specific recommendations."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
            path="/ai-insights?tab=predictions"
          />
          
          <AIInsightDashboardCard 
            title="Smart Recommendations" 
            description="Get advice specifically designed for teens on how to balance social media, gaming, and school requirements."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            path="/ai-insights?tab=recommendations"
          />
        </div>
      </div>

      {/* Quick actions */}
      <div className="bg-indigo-50 rounded-xl shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/focus" className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-indigo-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Start Homework Focus</span>
          </Link>
          
          <Link to="/habits" className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-indigo-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Set App Limits</span>
          </Link>
          
          <Link to="/settings" className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-indigo-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Sleep Guardian</span>
          </Link>
          
          <Link to="/ai-insights" className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-indigo-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-sm font-medium text-gray-700">AI Insights</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage; 